import { Head } from '@inertiajs/react'
import { useCallback, useMemo, useState } from 'react'

import LinedTextArea from '../../components/tools/LinedTextArea'
import ToolWorkspace from '../../components/tools/ToolWorkspace'
import {
  noopToolSwap,
  type ToolAbout,
  TOOL_CODEC_SELECT_CLASS,
  ToolIoChip,
  WrapLinesPanelSwitch,
} from '../../components/tools/toolChrome'
import {
  DEFAULT_UNICODE_CODEC_ID,
  getCodec,
  isUnicodeCodecId,
  MAX_CODEC_INPUT_CHARS,
  UNICODE_CODECS,
  type UnicodeCodecId,
} from '../../lib/codecs'

const UNICODE_ABOUT: ToolAbout = {
  description:
    'Normalize Unicode to NFC, NFD, NFKC, or NFKD. Text can look identical on screen but use different code points underneath. Normalization lines that up before you compare, search, or hash.',
  libraries: [
    {
      name: 'MDN: String.prototype.normalize',
      href: 'https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/String/normalize',
    },
  ],
}

export default function UnicodeTool() {
  const [codecId, setCodecId] = useState<UnicodeCodecId>(DEFAULT_UNICODE_CODEC_ID)
  const [mode, setMode] = useState<'encode' | 'decode'>('encode')
  const [input, setInput] = useState('')
  const [output, setOutput] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [lineWrap, setLineWrap] = useState(false)

  const codec = useMemo(() => getCodec(codecId) ?? UNICODE_CODECS[0], [codecId])

  const run = useCallback(async () => {
    try {
      setError(null)
      if (input.length > MAX_CODEC_INPUT_CHARS) {
        setError(`Input exceeds ${MAX_CODEC_INPUT_CHARS.toLocaleString()} characters (browser safety limit).`)
        return
      }
      setBusy(true)
      const out = await Promise.resolve(codec.encode(input))
      setOutput(out)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Normalize failed')
    } finally {
      setBusy(false)
    }
  }, [input, codec])

  const copy = useCallback(async () => {
    if (!output) return
    try {
      await navigator.clipboard.writeText(output)
    } catch {
      setError('Could not copy to clipboard')
    }
  }, [output])

  const onCodecChange = useCallback((id: string) => {
    if (!isUnicodeCodecId(id)) return
    setCodecId(id)
    setOutput('')
    setError(null)
  }, [])

  const inputColumn = (
    <LinedTextArea
      fillHeight
      value={input}
      onChange={(e) => setInput(e.target.value.slice(0, MAX_CODEC_INPUT_CHARS))}
      maxLength={MAX_CODEC_INPUT_CHARS}
      rows={14}
      wrapLines={lineWrap}
      placeholder={codec.placeholderEncode}
      aria-label="Unicode input"
    />
  )

  const toolSettings = <WrapLinesPanelSwitch lineWrap={lineWrap} onLineWrapChange={setLineWrap} />

  return (
    <>
      <Head title="Unicode normalization" />
      <ToolWorkspace
        heading="Unicode normalization"
        inputHeaderStart={<ToolIoChip>INPUT (RAW)</ToolIoChip>}
        algorithmSelector={
          <select
            value={codecId}
            onChange={(e) => onCodecChange(e.target.value)}
            aria-label="Normalization form"
            className={TOOL_CODEC_SELECT_CLASS}
          >
            {UNICODE_CODECS.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </select>
        }
        toolSettings={toolSettings}
        inputColumn={inputColumn}
        mode={mode}
        setMode={setMode}
        encodeOnly
        swapDisabled
        swapTitle="Not available"
        onSwap={noopToolSwap}
        onExecute={run}
        executeBusy={busy}
        output={output}
        onCopy={copy}
        error={error}
        statsCharValue={String(input.length).padStart(6, '0')}
        statsAlgorithm={codec.statsLabel}
        statsOutputLen={String(output.length).padStart(6, '0')}
        statsModeLabel="NORMALIZE"
        outputLineNumbers
        lineWrap={lineWrap}
        about={UNICODE_ABOUT}
      />
    </>
  )
}
